// NOTE: a dormant copy of this file lives at
// apps/mobile/_dormant-native-count/utils/sharedOrderMath.js — if that copy is
// ever revived, it should receive the flexible-source changes made here.
import { locationCode } from './locationCode.js';
import { validatePackageConsistency } from '../../../supabase/functions/_shared/packageBasis.mjs';
import { resolveRowPar, PAR_STATUS } from './countUnitModel.js';

function parseOptional(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

const round6 = (n) => Math.round(n * 1e6) / 1e6;

/**
 * The vendor-package constraint for a flexible-source item's ORDER math,
 * read from the purchase option the line resolves to (the same option the
 * vendor pill on the line shows). Null when there is no option or its
 * package size is unconfirmed — with no package there is nothing to round
 * to, and the order stays the plain NEED.
 *
 * @param {Object|null} option - a purchase_options row
 * @returns {{ optionId:*, qtyPerPackage:number, moqPackages:number|null,
 *             allowsFractional:boolean, orderIncrement:number|null }|null}
 */
export function flexiblePackageConstraint(option) {
  if (!option) return null;
  // The one canonical package definition (confirmed interpretation →
  // structured inner-pack columns → stored qty_per_package). A package whose
  // stored facts conflict rounds NOTHING — the line keeps the vendor-neutral
  // NEED rather than an order built from a disputed package size.
  const consistency = validatePackageConsistency(option);
  if (consistency.status === 'conflict') return null;
  const per = consistency.basis.quantityInInventoryUnit;
  if (per === null || per <= 0) return null;
  const moqRaw = parseOptional(option.moq_packages);
  const incRaw = parseOptional(option.order_increment);
  return {
    optionId: option.id ?? null,
    qtyPerPackage: per,
    moqPackages: moqRaw !== null && moqRaw > 0 ? Math.ceil(moqRaw) : null,
    allowsFractional: option.allows_fractional_packages === true,
    orderIncrement: incRaw !== null && incRaw > 0 ? incRaw : null,
  };
}

/**
 * Order quantity (in the item's count unit) for a flexible-source group.
 *
 * With a resolved vendor package the order is whole packages covering the
 * shortage, at least the option's MOQ — max(MOQ, ceil(need ÷ pkg)) × pkg —
 * because a 1.5-pail need against a 1-pail package is an order for 2 pails,
 * never 1.5. Mirrors orderPackagesForNeed / computePackages, which apply the
 * same rule at purchase allocation.
 *
 * Without a usable constraint the shortage passes through unrounded (the
 * vendor-neutral NEED), and a cut-to-order vendor (allowsFractional) keeps
 * the exact quantity, floored only by a stated MOQ.
 *
 * @param {number} shortage   PAR − on hand, in the item's count unit
 * @param {Object|null} constraint  from flexiblePackageConstraint()
 * @returns {number}
 */
export function flexibleOrderQty(shortage, constraint) {
  const need = parseOptional(shortage) ?? 0;
  if (need <= 0) return 0;
  const per = parseOptional(constraint?.qtyPerPackage);
  if (per === null || per <= 0) return need;
  const statedMoq = parseOptional(constraint?.moqPackages);
  const stated = statedMoq !== null && statedMoq > 0 ? statedMoq : null;
  const allowsFractional = constraint.allowsFractional === true;
  const incRaw = parseOptional(constraint?.orderIncrement);
  // The legal ordering step, in packages. Unstated means whole packages —
  // except cut-to-order, where the vendor cuts whatever is asked. A STATED
  // fractional step (0.25 lb on a by-the-pound option) steps even a
  // cut-to-order order: 6.1 lb becomes 6.25 lb, never 6.1.
  const increment = incRaw !== null && incRaw > 0 ? incRaw : allowsFractional ? null : 1;
  if (increment === null) {
    if (stated === null) return need;
    return round6(Math.max(Math.ceil(stated), need / per) * per);
  }
  // 1e-9 absorbs float noise: a need of exactly 3 against a 1.5-per-package
  // option must be 2 packages, not 3 (the same guard orderPackagesForNeed
  // uses). The MOQ snaps to the same grid — an integer-ceil first would turn
  // a 1.1-package minimum on a 0.25 step into 2 instead of the legal 1.25.
  const snapUp = (value) => round6(Math.ceil(value / increment - 1e-9) * increment);
  const moqLegal = stated !== null ? snapUp(stated) : allowsFractional ? 0 : snapUp(1);
  const packages = Math.max(moqLegal, snapUp(need / per));
  return round6(packages * per);
}

/**
 * Applies shared PAR/MOQ order math to an array of count items in bulk.
 *
 * Groups items by `inventory_item_id`, sums counted totals per group, then
 * recomputes `shortage`, `calculated_order_quantity`, and `order_quantity` for
 * every counted item using the group total (not the individual row total).
 *
 * Useful for hydration (load), zero-fill normalization, and similar batch paths.
 *
 * Counting always computes the exact vendor-neutral NEED = PAR − on hand.
 * Vendor package, MOQ, and increment rules are intentionally deferred until
 * Savings Review has selected an orderable product with a compatible package
 * basis. `flexibleSourceItemIds` is retained only to mark multi-vendor rows for
 * the UI; package constraints never change count-page quantities.
 *
 * Likewise, the item replenishment minimum/increment
 * (inventory_items.replenishment_minimum_qty / replenishment_increment_qty)
 * are ORDER-side facts: they shape the Savings Review PURCHASE TARGET
 * (parAndMoq.computePurchaseTarget), never the count-page NEED computed here.
 *
 * @param {Array} items - Array of count-item objects (all in the same session)
 * @param {Object} [opts] - Optional settings
 * @param {Set|Array} [opts.flexibleSourceItemIds] - inventory_item_id values
 *   whose groups use the flexible-source math above
 * @param {Object} [opts.packageConstraintsByItemId] - Legacy display metadata;
 *   accepted for call-site compatibility but never used to change NEED
 * @returns {Array} New array with updated shared order quantities for multi-location items
 */
export function applySharedOrderMathBulk(items, opts = {}) {
  const flexIds = normalizeIdSet(opts.flexibleSourceItemIds);
  const groups = {};
  items.forEach((item, idx) => {
    // Use the same fallback ID resolution as the interactive update path.
    const invId = resolveInvId(item);
    if (!invId) return;
    if (!groups[invId]) groups[invId] = [];
    groups[invId].push(idx);
  });

  const result = [...items];
  Object.entries(groups).forEach(([invId, indices]) => {
    const isFlexible = flexIds !== null && flexIds.has(String(invId));
    // The related-group fold (utils/countGroupFold) hides sibling members'
    // rows and stamps their contribution onto the favored member's first
    // row: hidden typed counts join the on-hand total, hidden PARs join the
    // max. The stamp rides exactly one row per group, so summing here never
    // double-counts. Unfolded sessions carry no stamps and nothing changes.
    const totalCounted = indices.reduce((sum, idx) => {
      const it = result[idx];
      const baseline = parseOptional(it?.group_baseline_count) ?? 0;
      return sum + baseline + (it.is_counted ? (parseFloat(it.current_count) || 0) : 0);
    }, 0);
    // Determine the canonical PAR as the max NORMALIZED value across ALL rows
    // in the group (counted or not). PAR is item-global within a session; if
    // rows diverge in the DB (e.g. legacy templates), normalize to the max.
    // Each row goes through resolveRowPar — stored par_normalized_quantity
    // wins, a legacy par_level counts only in the row's own count unit, and an
    // ambiguous legacy value ("2 cases", no package) contributes nothing
    // rather than entering the math as the number 2.
    let canonicalPar = 0;
    let canonicalParUnit = null;
    let canonicalParDefaulted = false;
    indices.forEach((idx) => {
      const it = result[idx];
      const resolution = resolveRowPar(it, rowCountUnit(it));
      if (resolution.status === PAR_STATUS.VALID && resolution.normalized !== null
        && resolution.normalized > canonicalPar) {
        canonicalPar = resolution.normalized;
        canonicalParUnit = resolution.unit || it?.par_unit || canonicalParUnit;
        canonicalParDefaulted = it?.par_defaulted === true;
      } else if (resolution.status === PAR_STATUS.VALID
        && resolution.normalized !== null
        && resolution.normalized === canonicalPar
        && it?.par_defaulted === true) {
        canonicalParDefaulted = true;
      }
      const floor = parseOptional(it?.group_par_floor);
      if (floor !== null && floor > canonicalPar) {
        canonicalPar = floor;
        canonicalParDefaulted = it?.group_par_defaulted === true;
      } else if (floor !== null && floor === canonicalPar && it?.group_par_defaulted === true) {
        canonicalParDefaulted = true;
      }
    });

    const sharedShortage = Math.max(0, canonicalPar - totalCounted);
    // Counting owns NEED, not BUY. Keep the exact vendor-neutral shortage for
    // both fixed- and flexible-source items; Savings Review resolves a vendor
    // package and applies package MOQ/increment later. Rounding here was how a
    // 9.5 LB shortage became a misleading "ORDER 10" before any package was
    // actually selected.
    const sharedCalcQty = round6(sharedShortage);

    // Stamp the canonical PAR onto every sibling row so display, edit, and
    // downstream math all read the same item-global value. The normalized
    // pair is stamped WITH it so the stamp stays resolvable on a second pass
    // (the display default 'each' on a unit-less row would otherwise turn
    // our own stamp into an unresolvable PAR — idempotence is pinned by
    // test). No legacy MOQ is stamped: the vendor minimum belongs to
    // purchase_options.moq_packages and is applied in Savings Review —
    // re-stamping inventory_items.moq here was the last way an item-level
    // minimum could leak into a display path.
    indices.forEach((idx) => {
      const it = result[idx];
      const manualQty = parseOptional(it.manual_order_quantity);
      const stamped = {
        ...it,
        par_level: canonicalPar > 0 ? canonicalPar : it.par_level,
        par_unit: it.par_unit || canonicalParUnit || 'each',
        ...(canonicalPar > 0 ? {
          par_normalized_quantity: canonicalPar,
          par_normalized_unit: canonicalParUnit || rowCountUnit(it) || it.par_unit || null,
          par_defaulted: canonicalParDefaulted,
        } : {}),
      };
      if (isFlexible) {
        stamped.flexible_source = true;
        stamped.flexible_order_rounded = false;
      }
      if (it.is_counted) {
        result[idx] = {
          ...stamped,
          shortage: sharedShortage,
          calculated_order_quantity: sharedCalcQty,
          order_quantity: manualQty ?? sharedCalcQty,
        };
      } else {
        result[idx] = stamped;
      }
    });
  });

  return result;
}

/**
 * Resolves the inventory-item ID from a count-item row.
 * Tries inventory_item_id first, then the snapshot id, then the row id itself.
 *
 * @param {Object} ci - A count item row
 * @returns {string|number|null}
 */
function resolveInvId(ci) {
  return ci?.inventory_item_id ?? ci?.item_snapshot?.id ?? ci?.id ?? null;
}

/**
 * The row's own count unit — what resolveRowPar compares par_unit against,
 * the same source order the group fold and countPresentation use. NO
 * par_unit fallback: a PAR's own unit compared against itself passes every
 * gate, which is how "2 cases" on a unit-less item became the number 2.
 */
function rowCountUnit(ci) {
  return ci?.item_snapshot?.unit ?? ci?.count_unit ?? '';
}

/**
 * Normalizes a Set/Array of inventory_item_id values into a Set of strings
 * (or null when absent), so membership checks work regardless of id type.
 *
 * @param {Set|Array|null|undefined} ids
 * @returns {Set<string>|null}
 */
function normalizeIdSet(ids) {
  if (!ids) return null;
  return new Set([...ids].map(String));
}

/**
 * Returns true when the row's canonical inventory item id (resolved the same
 * way as the grouping logic) is in the flexible-source id set.
 *
 * @param {Object} item - A count-item row
 * @param {Set|Array|null|undefined} flexibleSourceItemIds
 * @returns {boolean}
 */
export function isFlexibleSource(item, flexibleSourceItemIds) {
  const flexIds = normalizeIdSet(flexibleSourceItemIds);
  if (!flexIds) return false;
  const invId = resolveInvId(item);
  if (!invId) return false;
  return flexIds.has(String(invId));
}

/**
 * For an item that may appear in multiple locations within the same count session,
 * computes the shared (global) NEED by summing counted values across ALL
 * location rows for the same inventory item, then subtracting that total from PAR.
 *
 * If a per-location PAR override ever exists on the item record, the shared PAR
 * (par_level / expected_quantity from the current row) should still be used here;
 * per-location PAR overrides are not migrated or considered by this helper.
 *
 * Item replenishment minimum/increment shape the Savings Review purchase
 * target only — they never alter the count-page NEED this helper returns.
 *
 * The PAR is resolved from the item's own rows through resolveRowPar (stored
 * normalized value first, legacy par_level only in the row's own count unit) —
 * the `parLevel` argument survives as a fallback for callers whose in-memory
 * row does not yet carry the value they just typed, and is trusted only when
 * no row resolves, never over a normalized PAR.
 *
 * @param {string|number|null} inventoryItemId - The inventory item to aggregate
 * @param {Array}              allCountItems   - All count-item rows in the active session
 * @param {number}             currentItemIndex - Index of the row being updated (excluded
 *                                               from otherLocations, but its current_count
 *                                               is included in totalCounted)
 * @param {number}             parLevel        - Fallback PAR when no row resolves one
 * @param {number}             moq             - Legacy argument retained for callers;
 *                                               package minimums do not alter NEED
 * @param {boolean|Object}     [flexibleSource=false] - Legacy argument retained for
 *                                               callers; vendor choice does not alter NEED
 *
 * @returns {{
 *   totalCounted:    number,
 *   otherLocations:  Array<{ location: string, abbr: string, value: number }>,
 *   sharedOrderQty:  number,
 * }}
 */
export function computeSharedOrderData(
  inventoryItemId,
  allCountItems,
  currentItemIndex,
  parLevel,
  moq,
  flexibleSource = false,
) {
  const currentItem = allCountItems[currentItemIndex];
  const currentCountNum =
    parseFloat(currentItem?.current_count ?? currentItem?.actual_quantity) || 0;

  const otherLocations = [];

  allCountItems.forEach((ci, i) => {
    if (i === currentItemIndex) return;
    if (resolveInvId(ci) !== inventoryItemId) return;

    const hasCounted =
      ci.is_counted ||
      (ci.current_count !== '' && ci.current_count != null && ci.current_count !== undefined);
    if (!hasCounted) return;

    const location = ci.location || ci.item_snapshot?.location || 'Other';
    otherLocations.push({
      location,
      abbr: locationCode(location),
      value: parseFloat(ci.current_count) || 0,
    });
  });

  const siblingsTotal = otherLocations.reduce((s, e) => s + e.value, 0);
  // The related-group fold's stamps (see applySharedOrderMathBulk): hidden
  // sibling members' typed counts join the total, their max PAR joins the
  // PAR. Scanned across the item's rows because the stamp rides the first
  // row, which is not always the row being updated. The PAR itself is the
  // max NORMALIZED value across the item's rows (resolveRowPar — stored
  // normalized first, legacy par_level only in the row's own count unit);
  // the raw parLevel argument stands in only when no row resolves.
  let groupBaseline = 0;
  let groupParFloor = null;
  let resolvedPar = null;
  let statedUnresolvedPar = false;
  allCountItems.forEach((ci) => {
    if (resolveInvId(ci) !== inventoryItemId) return;
    const baseline = parseFloat(ci?.group_baseline_count);
    if (Number.isFinite(baseline)) groupBaseline += baseline;
    const floor = parseFloat(ci?.group_par_floor);
    if (Number.isFinite(floor) && (groupParFloor === null || floor > groupParFloor)) {
      groupParFloor = floor;
    }
    const resolution = resolveRowPar(ci, rowCountUnit(ci));
    if (resolution.status === PAR_STATUS.VALID && resolution.normalized !== null) {
      if (resolvedPar === null || resolution.normalized > resolvedPar) {
        resolvedPar = resolution.normalized;
      }
    } else if (resolution.display?.value != null) {
      statedUnresolvedPar = true;
    }
  });

  const totalCounted = siblingsTotal + currentCountNum + groupBaseline;
  // The raw parLevel argument stands in ONLY when no row states a PAR at all
  // (its value is then the same count-unit number the rows would resolve to).
  // When a row STATES a PAR the resolution refused — "2 cases", no package —
  // the raw number must not sneak back in through the argument: the PAR is a
  // question, and the need here is computed as if it were absent.
  const fallbackPar = statedUnresolvedPar ? 0 : (parseFloat(parLevel) || 0);
  const par = Math.max(resolvedPar ?? fallbackPar, groupParFloor ?? 0);
  const deficit = par - totalCounted;
  const sharedOrderQty = round6(Math.max(0, deficit));

  return { totalCounted, otherLocations, sharedOrderQty };
}
